import { offsetLimitPagination } from '@apollo/client/utilities';
import getObjectFromCache from './getObjectsFromCache';

const PAGINATED_ARGS = ['after', 'before', 'offset', 'first', 'last'];

const stableStringify = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

const getConnectionKey = (field) => {
  const directive = field?.directives?.find((dir) => dir.name.value === 'connection');
  if (!directive) return null;
  const keyArg = directive.arguments?.find((arg) => arg.name.value === 'key');
  return keyArg?.value?.value ?? null;
};

const collectSelectionNames = (selectionSet, names) => {
  if (!selectionSet) return;
  selectionSet.selections.forEach((selection) => {
    if (selection.kind === 'Field') {
      names.push(selection.name.value);
      collectSelectionNames(selection.selectionSet, names);
    } else if (selection.kind === 'FragmentSpread') {
      names.push(selection.name.value);
    } else if (selection.kind === 'InlineFragment') {
      collectSelectionNames(selection.selectionSet, names);
    }
  });
};

const selectionKeyCache = new WeakMap();

const getSelectionKey = (field) => {
  if (!field || !field.selectionSet) return '';
  const cached = selectionKeyCache.get(field);
  if (cached !== undefined) return cached;
  const names = [];
  collectSelectionNames(field.selectionSet, names);
  const key = [...new Set(names)].sort().join(',');
  selectionKeyCache.set(field, key);
  return key;
};

const getSelectionDiscriminator = (field) => getConnectionKey(field) ?? getSelectionKey(field);

const managerPaginationCache = (keyArgs, returnTypename, separateBySelection = false) => {
  const offsetPag = offsetLimitPagination();
  const read = (existing, options) => {
    if (!existing) {
      if (!returnTypename) return existing;
      if (options.args.id_In) {
        const cachedObjects = getObjectFromCache(options.cache, returnTypename, options.args.id_In);
        if (cachedObjects.length !== options.args.id_In.length) return {};
        return {
          pageInfo: {
            startCursor: null,
            endCursor: null,
            hasPreviousPage: false,
            hasNextPage: false,
          },
          totalPages: 1,
          totalCount: cachedObjects.length,
          edges: cachedObjects.map((obj) => ({
            node: obj,
            cursor: null,
          })),
        };
      }
      return existing;
    }
    const offset = options.args.offset || 0;
    const first = options.args.first || 10;
    const limit = Math.min(offset + first, existing.totalCount);
    if (limit > existing.edges.length) return {};
    const newReturn = existing.edges.slice(offset, limit);
    if (newReturn.length !== Object.values(newReturn).length) {
      return {};
    }
    return {
      ...existing,
      edges: newReturn,
      pageInfo: {
        ...existing.pageInfo,
        hasPreviousPage: offset > 0,
        hasNextPage: limit < existing.totalCount,
      },
      totalPages: Math.ceil(existing.totalCount / first),
    };
  };
  const merge = (existing, incoming, options) => {
    const newMerged = offsetPag.merge(existing?.edges, incoming.edges, options);
    return {
      ...incoming,
      edges: newMerged,
    };
  };
  const keyArgsFunction = (fields, context) => {
    const keyedArgNames = Object.keys(fields)
      .filter((field) => !PAGINATED_ARGS.includes(field))
      .sort();
    if (!separateBySelection) return keyedArgNames;
    const keyedArgs = keyedArgNames.reduce((acc, name) => {
      acc[name] = fields[name];
      return acc;
    }, {});
    return stableStringify({
      ...keyedArgs,
      __selection: getSelectionDiscriminator(context?.field),
    });
  };
  return {
    keyArgs: keyArgs || keyArgsFunction,
    read,
    merge,
  };
};

export default managerPaginationCache;
